import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { addDays, addMonths, format, isBefore, startOfDay, subMonths } from 'date-fns';
import { ReactComponent as ArrowDown } from 'assets/img/schedule-message/arrow-down.svg';
import { ReactComponent as ArrowUp } from 'assets/img/schedule-message/arrow-up.svg';
import { ReactComponent as CalIcon } from 'assets/img/schedule-message/calendar.svg';
import { ReactComponent as ChevronDown } from 'assets/img/schedule-message/chevron-down.svg';
import { ReactComponent as ClearIcon } from 'assets/img/schedule-message/close.svg';

const pad = (n) => String(n).padStart(2, '0');

const buildTimeOptions = () => {
  const opts = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const period = h < 12 ? 'AM' : 'PM';
      const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
      opts.push({ label: `${h12}:${pad(m)} ${period}`, h, m });
    }
  }
  return opts;
};

const makeDateTime = (date, h, m) => {
  const d = new Date(date);
  d.setHours(h, m, 0, 0);
  return d;
};

const fmtSendPreview = (dt) => format(dt, "EEE MMM d 'at' h:mma");

const normalizeTimeInput = (value) => {
  const raw = value?.trim().toLowerCase().replace(/\./g, '');
  if (!raw) return null;

  const match = raw.match(/^(\d{1,2})(?::?(\d{2}))?\s*(am|pm)?$/);
  if (!match) return null;

  let h = Number(match[1]);
  const m = match[2] === undefined ? 0 : Number(match[2]);
  const period = match[3];

  if (!Number.isInteger(h) || !Number.isInteger(m) || m < 0 || m > 59) return null;

  if (period) {
    if (h < 1 || h > 12) return null;
    if (period === 'pm' && h !== 12) h += 12;
    if (period === 'am' && h === 12) h = 0;
  } else if (h < 0 || h > 23) {
    return null;
  }

  const normalized = new Date();
  normalized.setHours(h, m, 0, 0);
  return { label: format(normalized, 'h:mm a'), h, m };
};

const getTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone.replace(/_/g, ' ');
  } catch {
    return 'Local time';
  }
};

const TimeSelect = ({ options, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value?.label || '');
  const [hasError, setHasError] = useState(false);
  const ref = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    setDraft(value?.label || '');
    setHasError(false);
  }, [value]);

  useEffect(() => {
    if (open && listRef.current) {
      const active = listRef.current.querySelector('.smp-opt--active');
      if (active) active.scrollIntoView({ block: 'nearest' });
    }
  }, [open]);

  const commitDraft = () => {
    const normalized = normalizeTimeInput(draft);
    if (!normalized) {
      setDraft(value?.label || '');
      setHasError(true);
      return;
    }

    setHasError(false);
    setDraft(normalized.label);
    onChange(normalized);
  };

  return (
    <div className="smp-custom-select" ref={ref}>
      <div className={`smp-custom-select__btn smp-custom-select__btn--input ${hasError ? 'smp-time-input--error' : ''}`}>
        <input
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            if (hasError) setHasError(false);
          }}
          onBlur={commitDraft}
          onFocus={() => setOpen(false)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commitDraft();
              setOpen(false);
              e.currentTarget.blur();
            }
            if (e.key === 'Escape') {
              setDraft(value?.label || '');
              setHasError(false);
              setOpen(false);
              e.currentTarget.blur();
            }
          }}
          aria-label="Schedule time"
        />
        <button
          type="button"
          className={`smp-custom-select__chevron ${open ? 'open' : ''}`}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setOpen((v) => !v)}
          aria-label="Show time options">
          <ChevronDown />
        </button>
      </div>

      {open && (
        <div className="smp-custom-select__menu" ref={listRef}>
          {options.map((opt, i) => (
            <button
              type="button"
              key={opt.label}
              className={`smp-custom-select__option ${opt.label === value?.label ? 'smp-opt--active' : ''}`}
              onClick={() => {
                onChange(opt);
                setOpen(false);
              }}>
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const ScheduleCalendarPanel = ({
  title = 'Schedule message',
  confirmLabel = 'Schedule Message',
  selectedDate,
  setSelectedDate,
  selectedTime,
  setSelectedTime,
  timeOptions,
  onConfirm,
  onClose,
  onSelectionChange,
  showTitle = false
}) => {
  const [month, setMonth] = useState(selectedDate || new Date());
  const previewDt =
    selectedDate && selectedTime ? makeDateTime(selectedDate, selectedTime.h, selectedTime.m) : null;
  const isInvalid = !previewDt || previewDt <= new Date();

  const notifySelection = (date, nextTime) => {
    if (!onSelectionChange || showTitle) return;
    const dt = date && nextTime ? makeDateTime(date, nextTime.h, nextTime.m) : null;
    if (dt && dt > new Date()) {
      onSelectionChange(dt);
    }
  };

  const handleSelectDate = (date) => {
    if (!date) return;
    setSelectedDate(date);
    setMonth(date);
    notifySelection(date, selectedTime);
  };

  const handleSelectTime = (nextTime) => {
    setSelectedTime(nextTime);
    notifySelection(selectedDate, nextTime);
  };

  const resetDate = () => {
    const tomorrow = addDays(new Date(), 1);
    setSelectedDate(tomorrow);
    setMonth(tomorrow);
  };

  return (
    <div className="smp-calendar-panel">
      {showTitle && (
        <div className="smp-calendar-panel__heading">
          <div>
            <h5 className="smp-modal__title">{title}</h5>
            <p className="smp-modal__tz">Time zone: {getTimezone()}</p>
          </div>
          <button type="button" className="smp-calendar-panel__close" onClick={onClose} aria-label="Close schedule picker">
            <ClearIcon />
          </button>
        </div>
      )}

      <div className="smp-calendar-panel__top">
        <div className="smp-date-pill">
          <CalIcon />
          <span>{selectedDate ? format(selectedDate, 'd MMMM') : 'Select date'}</span>
          <button type="button" onClick={resetDate} aria-label="Reset selected date">
            <ClearIcon />
          </button>
        </div>
        <TimeSelect options={timeOptions} value={selectedTime} onChange={handleSelectTime} />
      </div>

      <div className="smp-calendar-box">
        <div className="smp-calendar-box__header">
          <span>{format(month, 'MMMM yyyy')}</span>
          <div className="smp-calendar-box__controls">
            <button type="button" onClick={() => handleSelectDate(new Date())}>
              Today
            </button>
            <button type="button" aria-label="Previous month" onClick={() => setMonth((m) => subMonths(m, 1))}>
              <ArrowUp />
            </button>
            <button type="button" aria-label="Next month" onClick={() => setMonth((m) => addMonths(m, 1))}>
              <ArrowDown />
            </button>
          </div>
        </div>

        <DayPicker
          mode="single"
          selected={selectedDate}
          onSelect={handleSelectDate}
          month={month}
          onMonthChange={setMonth}
          disabled={(date) => isBefore(startOfDay(date), startOfDay(new Date()))}
          hideNavigation
          showOutsideDays
        />
      </div>

      <div className="smp-calendar-panel__footer">
        {previewDt ? (
          <span className="smp-calendar-panel__send-copy">
            Sends <strong>{fmtSendPreview(previewDt)}</strong>
          </span>
        ) : (
          <span>Choose a date and time</span>
        )}
        {showTitle && (
          <button type="button" className="smp-btn smp-btn--confirm" disabled={isInvalid} onClick={onConfirm}>
            {confirmLabel}
          </button>
        )}
      </div>
    </div>
  );
};

const ScheduleMessagePicker = ({ onSchedule, openModalDirectly = false, onCancel, initialScheduledAt }) => {
  const [showCalendar, setShowCalendar] = useState(openModalDirectly);
  const wrapperRef = useRef(null);
  const allTimes = useMemo(() => buildTimeOptions(), []);
  const getInitialSelection = () => {
    const parsed = initialScheduledAt ? new Date(initialScheduledAt) : null;
    if (parsed && !Number.isNaN(parsed.getTime())) {
      return {
        date: parsed,
        time: { label: format(parsed, 'h:mm a'), h: parsed.getHours(), m: parsed.getMinutes() }
      };
    }

    return { date: addDays(new Date(), 1), time: allTimes[18] };
  };
  const initialSelection = getInitialSelection();
  const [selectedDate, setSelectedDate] = useState(initialSelection.date);
  const [selectedTime, setSelectedTime] = useState(initialSelection.time);

  useEffect(() => {
    if (!openModalDirectly) return;
    const nextSelection = getInitialSelection();
    setSelectedDate(nextSelection.date);
    setSelectedTime(nextSelection.time);
    setShowCalendar(true);
  }, [initialScheduledAt, openModalDirectly]);

  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        if (openModalDirectly && e.target.closest('.sml-actions')) return;
        if (openModalDirectly) {
          handleClose();
        } else {
          setShowCalendar(false);
        }
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openModalDirectly, onCancel]);

  const handleClose = () => {
    setShowCalendar(false);
    if (onCancel) onCancel();
  };

  const confirmCalendar = () => {
    const dt = makeDateTime(selectedDate, selectedTime.h, selectedTime.m);
    if (dt <= new Date()) return;
    setShowCalendar(false);
    onSchedule(dt.toISOString());
  };

  if (openModalDirectly) {
    return (
      <div className="smp-inline-reschedule" ref={wrapperRef}>
        {showCalendar && (
          <ScheduleCalendarPanel
            title="Reschedule message"
            confirmLabel="Reschedule"
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            selectedTime={selectedTime}
            setSelectedTime={setSelectedTime}
            timeOptions={allTimes}
            onConfirm={confirmCalendar}
            onClose={handleClose}
            showTitle
          />
        )}
      </div>
    );
  }

  return (
    <div className="smp-wrapper" ref={wrapperRef}>
      <button
        type="button"
        className="smp-trigger"
        onClick={() => setShowCalendar((v) => !v)}
        title="Schedule message"
        aria-expanded={showCalendar}>
        <ChevronDown />
      </button>

      {showCalendar && (
        <div className="smp-calendar-popover">
          <ScheduleCalendarPanel
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            selectedTime={selectedTime}
            setSelectedTime={setSelectedTime}
            timeOptions={allTimes}
            onConfirm={confirmCalendar}
            onClose={() => setShowCalendar(false)}
            onSelectionChange={(dt) => onSchedule(dt.toISOString())}
          />
        </div>
      )}
    </div>
  );
};

export default ScheduleMessagePicker;
